/**
 * Earthworks warnings (EW-4, fieldiq-api#1284): no locate on file, a locate
 * expired, no ground-disturbance cert, no demolition permit, a permit
 * expired, no hazmat assessment.
 *
 * A WARNING NEVER BLOCKS WORK. It is shown, in amber, with who has already
 * carried on past it. On the phone each card has an explicit "Carry on" tap;
 * the kinds tapped ride along with the log save as `acknowledge_warnings`.
 * Nothing here — and nothing that uses it — disables Save because a warning
 * is showing.
 */
import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { ewInstant } from '../../lib/earthworks';
import type { EwFieldWarning, EwWarning } from '../../types/earthworks';

export interface EwWarningView {
  kind: string;
  label: string;
  message: string;
  acks: { userId: string; userName: string | null; at: string | null }[];
}

export const fromFieldWarning = (w: EwFieldWarning): EwWarningView => ({
  kind: w.kind,
  label: w.label,
  message: w.message,
  acks: (w.acknowledgements ?? []).map((a) => ({ userId: a.user_id, userName: a.user_name, at: a.acknowledged_at })),
});

export const fromOfficeWarning = (w: EwWarning): EwWarningView => ({
  kind: w.kind,
  label: w.label,
  message: w.message,
  acks: (w.acknowledgements ?? []).map((a) => ({ userId: a.userId, userName: a.userName, at: a.acknowledgedAt })),
});

/**
 * The warnings, each as an amber card. With `onToggle`, each card offers
 * "Carry on" (or says the caller already did); without it, the list is a read.
 */
export function EwWarningCards({
  warnings,
  timeZone,
  userId,
  carried,
  onToggle,
  testId = 'ew-warnings',
}: {
  warnings: EwWarningView[];
  timeZone: string | null;
  userId?: string | null;
  carried?: ReadonlySet<string>;
  onToggle?: (kind: string) => void;
  testId?: string;
}) {
  if (warnings.length === 0) return null;
  return (
    <section aria-label="Warnings" data-testid={testId} className="space-y-2">
      {warnings.map((w) => {
        const mine = !!userId && w.acks.some((a) => a.userId === userId);
        const tapped = !!carried?.has(w.kind);
        return (
          <div
            key={w.kind}
            data-testid="ew-warning"
            data-kind={w.kind}
            className="rounded-fi-control border border-fi-amber/60 border-l-4 border-l-fi-amber bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text"
          >
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 size-5 shrink-0 text-fi-amber" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="font-extrabold text-white">{w.label}</p>
                <p className="mt-0.5">{w.message}</p>
                {w.acks.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-xs text-fieldiq-muted" data-testid="ew-warning-acks">
                    {w.acks.map((a, i) => (
                      <li key={`${a.userId}-${i}`}>
                        Carried on by {a.userName || '—'} · {ewInstant(a.at, timeZone)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {onToggle &&
              (mine ? (
                <p className="mt-2 flex items-center gap-1.5 text-xs font-bold text-fieldiq-text">
                  <CheckCircle2 className="size-4 text-fi-green" aria-hidden="true" />
                  You have carried on past this.
                </p>
              ) : (
                <button
                  type="button"
                  aria-pressed={tapped}
                  data-testid="ew-warning-carry-on"
                  onClick={() => onToggle(w.kind)}
                  className={`mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-fi-control border px-3 text-sm font-extrabold ${
                    tapped ? 'border-fi-green bg-fi-green/15 text-white' : 'border-fi-amber bg-fieldiq-dark text-fi-amber'
                  }`}
                >
                  {tapped ? (
                    <>
                      <CheckCircle2 className="size-4" aria-hidden="true" />
                      Carrying on — recorded when you save
                    </>
                  ) : (
                    'Carry on'
                  )}
                </button>
              ))}
          </div>
        );
      })}
    </section>
  );
}
